import { useEffect, useState } from 'react'
import { api } from '../../services/api.js'
import UpgradeInline from '../shared/UpgradeInline.jsx'

const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.genealogic.ai'

export default function InviteModal({ treeId, onClose }) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('editor')
  const [sendEmail, setSendEmail] = useState(false)
  const [creating, setCreating] = useState(false)
  const [link, setLink] = useState(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)
  const [tierError, setTierError] = useState(null)
  const [members, setMembers] = useState([])
  const [invites, setInvites] = useState([])
  const [loadingMembers, setLoadingMembers] = useState(true)

  useEffect(() => {
    api.get(`/trees/${treeId}/invites`)
      .then(data => { setMembers(data.members); setInvites(data.invites) })
      .catch(() => {})
      .finally(() => setLoadingMembers(false))
  }, [treeId])

  async function createInvite(e) {
    e.preventDefault()
    setCreating(true)
    setError(null)
    setTierError(null)
    try {
      const res = await api.post(`/trees/${treeId}/invites`, {
        email: email.trim() || undefined,
        role,
        send_email: sendEmail && !!email.trim(),
      })
      setLink(res.link)
      setInvites(prev => [res.invite, ...prev])
      setEmail('')
    } catch (err) {
      if (err.code === 'TIER_LIMIT') setTierError(err)
      else setError(err.message || 'Failed to create invite')
    } finally {
      setCreating(false)
    }
  }

  async function revokeInvite(id) {
    await api.delete(`/trees/${treeId}/invites/${id}`).catch(() => {})
    setInvites(prev => prev.filter(i => i.id !== id))
  }

  async function removeMember(userId) {
    if (!confirm('Remove this person from the tree?')) return
    await api.delete(`/trees/${treeId}/members/${userId}`).catch(() => {})
    setMembers(prev => prev.filter(m => m.user_id !== userId))
  }

  async function changeRole(userId, newRole) {
    await api.patch(`/trees/${treeId}/members/${userId}`, { role: newRole }).catch(() => {})
    setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role: newRole } : m))
  }

  function copyLink() {
    navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()} role="dialog">
        <div className="modal__header">
          <div className="modal__title">Invite & members</div>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="modal__body">

          {/* Create invite */}
          <div style={{ marginBottom: '1.75rem' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1rem', lineHeight: '1.5' }}>
              Generate a link, then share it via copy, WhatsApp, or email.
              Entering an email here is optional and only records who the invite is for.
            </p>
            <form onSubmit={createInvite}>
              <div className="form-field" style={{ marginBottom: '0.75rem' }}>
                <label className="form-field__label">Email address (optional)</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="colleague@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
              <div className="form-field" style={{ marginBottom: '0.75rem' }}>
                <label className="form-field__label">Permission</label>
                <select
                  className="form-input form-input--select"
                  value={role}
                  onChange={e => setRole(e.target.value)}
                >
                  <option value="editor">Can edit — add and change persons</option>
                  <option value="viewer">Can view — read only</option>
                </select>
              </div>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                fontSize: '0.82rem', color: 'var(--text-muted)',
                marginBottom: '1rem', cursor: email.trim() ? 'pointer' : 'not-allowed',
                opacity: email.trim() ? 1 : 0.5,
              }}>
                <input
                  type="checkbox"
                  checked={sendEmail}
                  disabled={!email.trim()}
                  onChange={e => setSendEmail(e.target.checked)}
                />
                Also send the invite by email now
              </label>
              {tierError && <UpgradeInline error={tierError} className="upgrade-inline--modal" />}
              {error && <div className="form-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? 'Creating…' : 'Create invite link'}
              </button>
            </form>
          </div>

          {/* Generated link */}
          {link && (
            <div style={{
              background: 'var(--surface)', borderRadius: '10px',
              padding: '1rem', marginBottom: '1.25rem',
              border: '1px solid var(--border)',
            }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.6rem',
                fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Invite link — valid for 7 days
              </div>

              {/* Link field with inline copy */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                background: 'white', border: '1px solid var(--border)',
                borderRadius: '6px', padding: '0.45rem 0.6rem',
                marginBottom: '0.75rem',
              }}>
                <code style={{ fontSize: '0.78rem', color: 'var(--text)', flex: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  userSelect: 'all', cursor: 'text' }}>
                  {link}
                </code>
              </div>

              {/* Share actions */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={copyLink}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                    padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '0.82rem', fontWeight: 600, border: 'none',
                    background: copied ? '#16a34a' : 'var(--green)',
                    color: 'white', transition: 'background 0.2s', flexShrink: 0,
                  }}
                >
                  {copied ? '✓ Copied!' : '⎘ Copy link'}
                </button>

                <a
                  href={`https://wa.me/?text=${encodeURIComponent('Join my family tree on Genealogic: ' + link)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                    padding: '0.5rem 1rem', borderRadius: '6px',
                    fontSize: '0.82rem', fontWeight: 600,
                    background: '#25D366', color: 'white',
                    textDecoration: 'none', flexShrink: 0,
                  }}
                >
                  WhatsApp
                </a>

                <a
                  href={`mailto:?subject=${encodeURIComponent('Join my family tree on Genealogic')}&body=${encodeURIComponent('I\'d like to invite you to collaborate on my family tree.\n\n' + link)}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                    padding: '0.5rem 1rem', borderRadius: '6px',
                    fontSize: '0.82rem', fontWeight: 600,
                    background: 'var(--surface)', color: 'var(--text)',
                    border: '1px solid var(--border)', textDecoration: 'none', flexShrink: 0,
                  }}
                >
                  Email
                </a>
              </div>
            </div>
          )}

          {/* Active invites */}
          {invites.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.25rem', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
                Pending invites
              </div>
              {invites.map(inv => (
                <div key={inv.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.5rem 0.75rem', borderRadius: '6px',
                  border: '1px solid var(--border)', marginBottom: '0.4rem',
                  fontSize: '0.82rem',
                }}>
                  <div>
                    <span style={{ color: 'var(--text)' }}>{inv.email || 'Link invite'}</span>
                    <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                      · {inv.role === 'editor' ? 'Can edit' : 'Can view'}
                    </span>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ color: '#dc2626', fontSize: '0.75rem' }}
                    onClick={() => revokeInvite(inv.id)}
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Members */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.25rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
              Members
            </div>
            {loadingMembers ? (
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Loading…</div>
            ) : members.length === 0 ? (
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                No members yet. Share an invite link to collaborate.
              </div>
            ) : (
              members.map(m => (
                <div key={m.user_id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.5rem 0.75rem', borderRadius: '6px',
                  border: '1px solid var(--border)', marginBottom: '0.4rem',
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'var(--green)', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.72rem', fontWeight: 600, flexShrink: 0,
                  }}>
                    {m.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text)' }}>{m.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{m.email}</div>
                  </div>
                  <select
                    className="form-input form-input--select"
                    value={m.role}
                    onChange={e => changeRole(m.user_id, e.target.value)}
                    style={{ width: '110px', fontSize: '0.78rem', padding: '0.3rem 0.5rem' }}
                  >
                    <option value="editor">Can edit</option>
                    <option value="viewer">Can view</option>
                  </select>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ color: '#dc2626', fontSize: '0.75rem', flexShrink: 0 }}
                    onClick={() => removeMember(m.user_id)}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
